import {
  AfterAddNewFieldsTransformationExampleRows,
  AfterFlattenTransformationExampleRows
} from "./constants/transformationInputOutput";
import { DestinationSelectInputField } from "@/components/custom/InputField/WorkflowSelectInput";
import { NestedDropdown } from "@/components/custom/MultiLayeredDropdownField";
import { Button } from "@/components/ui/button";
import { FormLabel } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ButtonVariant } from "@/constants/button/button";
import { INPUT_FIELD_CLASSNAME } from "@/constants/classname/classname";
import { AppFieldsType } from "@/constants/workflows/workflowCreate";
import { getAppFieldsAccordingToType } from "@/helpers/appFields";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import { Plus, Trash2 } from "lucide-react";
import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { RootState } from "@/redux";
import { AddNewFieldsProps, AddNewFieldsRef, NewFieldsConfig } from "./types/addNewFields";

export const AddNewFields = forwardRef<AddNewFieldsRef, AddNewFieldsProps>(({
  allFields,
  initialConfiguration,
}, ref) => {
  const { applications } = useSelector((state: RootState) => state.application);
  const createEmptyAggregation = (suffix?: string): NewFieldsConfig => ({
    id: `aggregation-${Date.now()}${suffix ? `-${suffix}` : ""}`,
    fieldLabel: "",
    fieldOperation: "",
    fields: [
      {
        sourceField: "",
        sourceFieldLabel: "",
      },
    ],
    priority: "",
  });

  const [aggregations, setAggregations] = useState<NewFieldsConfig[]>(() => {
    if (initialConfiguration?.fieldConfigs && initialConfiguration.fieldConfigs.length > 0) {
      return initialConfiguration.fieldConfigs.map((config, idx) => ({
        id: `aggregation-${Date.now()}-${idx}`,
        fieldLabel: config.fieldName || "",
        fieldOperation: config.tcsOperation || "",
        fields: Array.isArray(config.fields)
          ? config.fields.map((field) => ({
            sourceField: field || "",
            sourceFieldLabel: field || "",
          }))
          : [{ sourceField: "", sourceFieldLabel: "" }],
        priority: config.priority || "",
      }));
    }

    return [createEmptyAggregation()];
  });
  const [hasError, setHasError] = useState(false);

  useImperativeHandle(ref, () => ({
    validate: () => {
      // Check if any aggregation has a field label
      const hasFilledAggregation = aggregations.some(agg => agg.fieldLabel.trim() !== "");

      // If no field labels are filled, validation passes (all optional)
      if (!hasFilledAggregation) {
        setHasError(false);
        return true;
      }

      // If any aggregation has a field label, validate that it has operation and at least one field
      const isValid = aggregations.every(agg => {
        // Skip empty aggregations
        if (agg.fieldLabel.trim() === "") {
          return true;
        }

        // Check if operation is selected
        if (agg.fieldOperation.trim() === "") {
          return false;
        }

        // getCurrentTimestamp must not have any fields configured at all.
        // (User should delete the default empty field row.)
        if (agg.fieldOperation.trim() === 'getCurrentTimestamp') {
          return agg.fields.length === 0;
        }

        // Check if at least one field is selected
        const hasValidField = agg.fields.some(field => field.sourceField.trim() !== "");
        return hasValidField;
      });

      setHasError(!isValid);
      return isValid;
    },
    getData: () => {
      const fieldConfigs = aggregations
        .filter(agg => agg.fieldLabel.trim() !== "")
        .map(agg => ({
          fieldName: agg.fieldLabel.trim(),
          tcsOperation: agg.fieldOperation.trim(),
          fields: agg.fieldOperation.trim() === 'getCurrentTimestamp'
            ? []
            : agg.fields
              .map(field => field.sourceField.trim())
              .filter(Boolean),
          priority: agg.priority || ""
        }))
        .filter(config =>
          config.tcsOperation !== "" &&
          (config.tcsOperation === 'getCurrentTimestamp'
            ? config.fields.length === 0
            : config.fields.length > 0)
        );

      return {
        fieldConfigs
      };
    }
  }));

  const selectOptions = [
    { id: 'getCurrentTimestamp', value: 'Get Current Timestamp', description: 'Get the current date and time as timestamp (uses America/New_York timezone)' },
    { id: 'getMinimumValue', value: 'Get Minimum value', description: 'Get minimum value among the selected field values' },
    { id: 'getMaximumValue', value: 'Get Maximum value', description: 'Get maximum value among the selected field values' },
    { id: 'getAverageValue', value: 'Get Average value', description: 'Get average value among the selected field values' },
    { id: 'getCount', value: 'Get Count', description: 'Get count of unique values among the selected field values' },
    { id: 'resolveValueByPriority', value: 'Resolve Value by Priority', description: 'Returns the first non-empty value from the selected fields in order of priority' },
    { id: 'splitValuesByComma', value: 'Split Values by Comma', description: 'Split values by a comma delimiter and store as an array' },
    { id: 'splitByCommaAndGetUnique', value: 'Split by Comma and Get Unique', description: 'Split values by a comma delimiter, get unique values from them' }
  ]

  const responseFieldTree = useMemo(
    () => getAppFieldsAccordingToType(allFields, AppFieldsType.RESPONSE_TYPE, applications),
    [allFields, applications]
  );

  return (
    <div className="text-primary flex flex-col max-h-[500px] min-h-[300px] h-full">
      <div className="flex-1 overflow-y-auto thin-scrollbar pr-2">
        <div className="flex w-full mb-4">
          <div className="w-1/2 flex-col items-start">
            <p className="text-sm font-medium">
              Example Rows
            </p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterFlattenTransformationExampleRows, null, 2)}
              </pre>
            </div>
          </div>
          <div className="mx-1" />
          <div className="w-1/2">
            <p className="text-sm font-medium">Transformed rows after Grouping By Family Id</p>
            <div className="bg-foreground rounded-md border p-4 mt-1 max-h-[250px] overflow-auto thin-scrollbar">
              <pre className="text-xs">
                {JSON.stringify(AfterAddNewFieldsTransformationExampleRows, null, 2)}
              </pre>
            </div>
          </div>
        </div>
        <div className="pr-2 space-y-4">
          {aggregations.map((aggregation, aggregationIndex) => (
            <div key={aggregation.id} className="rounded-md border border-accent bg-accent-background p-4">
              <div className="flex flex-wrap items-center">
                <div className="min-w-[240px]">
                  <FormLabel className="flex justify-start font-semibold text-primary !text-sm">
                    Field label
                  </FormLabel>
                  <p className="text-secondary text-sm font-normal -mt-1 mb-1 text-left">
                    Enter field label to be created (e.g. FamilyEnrolledSessions).
                  </p>
                  <Input
                    className={cn(
                      INPUT_FIELD_CLASSNAME,
                      "bg-foreground max-w-[280px]",
                      aggregation.fieldLabel && !aggregation.fieldOperation && hasError ? "!border-destructive !bg-destructive-foreground" : ""
                    )}
                    type="text"
                    value={aggregation.fieldLabel}
                    placeholder="e.g. LastYearAttended"
                    onChange={(event) => {
                      const { value } = event.target;
                      setHasError(false);
                      setAggregations((prev) =>
                        prev.map((item, idx) =>
                          idx === aggregationIndex
                            ? {
                              ...item,
                              fieldLabel: value,
                            }
                            : item
                        )
                      );
                    }}
                  />
                </div>
                <div className="mx-2" />
                <div className="min-w-[220px] max-w-[280px]">
                  <DestinationSelectInputField
                    label="Field operation"
                    labelDescription="Choose How you want to generate value as"
                    value={aggregation.fieldOperation}
                    selectOptions={selectOptions}
                    onChange={(value) => {
                      setHasError(false);
                      setAggregations((prev) =>
                        prev.map((item, idx) =>
                          idx === aggregationIndex
                            ? {
                              ...item,
                              fieldOperation: value,
                            }
                            : item
                        )
                      );
                    }}
                    additionalClassName="bg-foreground"
                    hasError={aggregation.fieldLabel.trim() !== "" && !aggregation.fieldOperation && hasError}
                  />
                </div>
                {aggregation.fieldOperation.trim() === 'resolveValueByPriority' && (
                  <div className="min-w-[220px] max-w-[280px]">
                    <FormLabel className="flex justify-start font-semibold text-primary !text-sm">
                      Priority
                    </FormLabel>
                    <p className="text-secondary text-sm font-normal -mt-1 mb-1 text-left">
                      (Optional) Enter priority logic or string (e.g., &quot;11&gt;2024&quot;)
                    </p>
                    <Input
                      className={cn(INPUT_FIELD_CLASSNAME, "bg-foreground max-w-[280px]")}
                      type="text"
                      value={aggregation.priority}
                      placeholder="e.g. 11>2024"
                      onChange={(event) => {
                        setAggregations((prev) =>
                          prev.map((item, idx) =>
                            idx === aggregationIndex
                              ? { ...item, priority: event.target.value }
                              : item
                          )
                        );
                      }}
                    />
                  </div>
                )}
                {aggregations.length > 1 && (
                  <Button
                    variant={ButtonVariant.ICON}
                    className="ml-auto"
                    onClick={() =>
                      setAggregations((prev) => prev.filter((_, idx) => idx !== aggregationIndex))
                    }
                  >
                    <Trash2 className="text-destructive" size={16} />
                  </Button>
                )}
              </div>

              <div className="mt-4">
                <FormLabel className="flex justify-start font-semibold text-primary !text-sm">
                  Add all fields that are needed for the above operation
                </FormLabel>
                <p className="text-secondary text-sm font-normal -mt-1 mb-2">
                  E.g. "Family Enrolled Sessions" need all camper (C1-C4) enrolled sessions fields
                </p>
                {aggregation.fieldLabel.trim() !== "" &&
                  aggregation.fieldOperation.trim() === 'getCurrentTimestamp' &&
                  aggregation.fields.length > 0 &&
                  hasError && (
                    <p className="text-destructive text-sm font-normal mb-2">
                      Get Current Timestamp doesn't require any fields. Please remove all fields below.
                    </p>
                  )}
                {aggregation.fields.map((field, fieldIndex) => (
                  <div key={`${aggregation.id}-field-${fieldIndex}`} className="flex flex-wrap items-start gap-4">
                    <div className="min-w-[240px] flex-1 max-w-[280px] mb-2">
                      <NestedDropdown
                        data={responseFieldTree}
                        field={field.sourceField}
                        label="Field"
                        initialSelectedLabel={field.sourceFieldLabel}
                        showErrorFields={
                          aggregation.fieldLabel.trim() !== "" &&
                          aggregation.fieldOperation.trim() !== "" &&
                          ((aggregation.fieldOperation.trim() === 'getCurrentTimestamp' && aggregation.fields.length > 0) ||
                            !aggregation.fields.some(f => f.sourceField.trim() !== "")) &&
                          hasError
                        }
                        onSelect={(item) => {
                          setHasError(false);
                          setAggregations((prev) =>
                            prev.map((config, cfgIdx) => {
                              if (cfgIdx !== aggregationIndex) {
                                return config;
                              }

                              return {
                                ...config,
                                fields: config.fields.map((fieldItem, fldIdx) =>
                                  fldIdx === fieldIndex
                                    ? {
                                      sourceField: item.codeChain,
                                      sourceFieldLabel: item.displayName,
                                    }
                                    : fieldItem
                                ),
                              };
                            })
                          );
                        }}
                      />
                    </div>
                    {aggregation.fields.length > 0 && (
                      <Button
                        variant={ButtonVariant.ICON}
                        onClick={() =>
                          setAggregations((prev) =>
                            prev.map((config, cfgIdx) => {
                              if (cfgIdx !== aggregationIndex) {
                                return config;
                              }

                              return {
                                ...config,
                                fields: config.fields.filter((_, fldIdx) => fldIdx !== fieldIndex),
                              };
                            })
                          )
                        }
                      >
                        <Trash2 className="text-destructive mt-10" size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  className="font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent"
                  variant={ButtonVariant.OUTLINE}
                  onClick={() =>
                    setAggregations((prev) =>
                      prev.map((config, cfgIdx) =>
                        cfgIdx === aggregationIndex
                          ? {
                            ...config,
                            fields: [
                              ...config.fields,
                              {
                                sourceField: "",
                                sourceFieldLabel: "",
                              },
                            ],
                          }
                          : config
                      )
                    )
                  }
                >
                  <Plus className="mr-2 w-4 h-4" />
                  Add field to this operation
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Button
        type="button"
        className="mt-4 font-semibold text-accent border-accent bg-background hover:text-accent hover:border-accent self-start"
        variant={ButtonVariant.OUTLINE}
        onClick={() =>
          setAggregations((prev) => [
            ...prev,
            createEmptyAggregation(Math.random().toString(36).slice(2, 8)),
          ])
        }
      >
        <Plus className="mr-2 w-4 h-4" />
        Add another aggregation
      </Button>
    </div>
  );
});
